(() => {
  if (customElements.get('dg-product-view')) return;
  class DualityProduct extends HTMLElement {
    /** Builds "Home / Parent / Collection / Product" from the category tree, using the product's deepest collection. */
    renderBreadcrumb() {
      const crumb = this.querySelector('[data-pdp-crumb]');
      const data = this.querySelector('[data-dg-categories]');
      if (!crumb || !data) return;
      let nodes;
      try { nodes = JSON.parse(data.textContent); } catch { return; }
      const byHandle = new Map(nodes.map((node) => [node.handle, node]));
      const trailFor = (node) => {
        const trail = [];
        for (let current = node; current; current = byHandle.get(current.parent)) trail.unshift(current);
        return trail;
      };
      let trail = [];
      for (const handle of crumb.dataset.collections.split(',')) {
        const node = byHandle.get(handle);
        if (node && trailFor(node).length > trail.length) trail = trailFor(node);
      }
      if (!trail.length) return;
      const current = crumb.lastElementChild;
      for (const node of trail) {
        const link = document.createElement('a');
        link.href = crumb.dataset.collectionsUrl + '/' + node.handle;
        link.textContent = node.title;
        const separator = document.createElement('span');
        separator.textContent = '/';
        crumb.insertBefore(link, current);
        crumb.insertBefore(separator, current);
      }
    }

    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';
      this.renderBreadcrumb();
      const selector = this.querySelector('[data-dg-variant]');
      const main = this.querySelector('[data-dg-detail-image]');
      const zoom = this.querySelector('[data-pdp-zoom]');
      const zoomImage = this.querySelector('[data-pdp-zoom-image]');
      const zoomButton = this.querySelector('[data-pdp-zoom-open]');
      const gallery = Array.from(this.querySelectorAll('[data-dg-gallery]'));
      const galleryUrls = gallery.map(button => button.dataset.dgGallery);
      const galleryLabels = document.querySelector('[data-dg-product-labels]')?.dataset || {};
      const previous = document.createElement('button');
      previous.type = 'button';
      previous.className = 'dg-zoom-arrow dg-zoom-previous';
      previous.setAttribute('aria-label', galleryLabels.previousImage || '←');
      previous.textContent = '‹';
      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'dg-zoom-arrow dg-zoom-next';
      next.setAttribute('aria-label', galleryLabels.nextImage || '→');
      next.textContent = '›';
      const imageCounter = document.createElement('p');
      imageCounter.className = 'dg-zoom-counter';
      imageCounter.setAttribute('role', 'status');
      imageCounter.setAttribute('aria-live', 'polite');
      previous.hidden = next.hidden = galleryUrls.length < 2;
      zoom.append(previous, next, imageCounter);
      let zoomIndex = 0;
      const syncZoom = () => {
        if (!main) return;
        zoomIndex = Math.max(0, galleryUrls.indexOf(main.getAttribute('src')));
        zoomImage.src = galleryUrls[zoomIndex] || main.currentSrc || main.src;
        zoomImage.alt = main.alt;
        imageCounter.textContent = (galleryLabels.imageLabel || '') + ' ' + (zoomIndex + 1) + ' / ' + Math.max(1, galleryUrls.length);
      };
      const quantity = this.querySelector('input[name="quantity"]');
      const cart = this.querySelector('[data-pdp-cart], .dg-pdp-cart');
      const isPreview = this.dataset.preview === 'true';
      const optionLabel = selector?.closest('.dg-pdp-option');
      const optionName = optionLabel?.querySelector('span')?.textContent.trim() || '';
      const isConditionOption = /^(artikel)?zustand$/i.test(optionName);
      let conditionPicker = null;
      const optionNames = JSON.parse(selector?.dataset.optionNames || '[]');
      const variants = selector ? Array.from(selector.options, option => JSON.parse(option.dataset.pdpValue)) : [];
      // Every product with real options gets the step boxes; default-variant products keep the hidden select.
      if (selector && optionLabel && !optionLabel.hidden && optionNames.length > 0) {
        // Amazon-style steps: pick each option (e.g. Lieferumfang, then Zustand) in its own box.
        const indexed = variants.map((variant, index) => ({variant, index}));
        const matchesBefore = (variant, reference, position) => variant.options.slice(0, position).every((value, k) => value === reference.options[k]);
        const resolve = (position, value) => {
          const current = variants[selector.selectedIndex];
          const wanted = current.options.slice();
          wanted[position] = value;
          const candidates = indexed.filter(({variant}) => variant.options[position] === value && matchesBefore(variant, current, position));
          const exact = candidates.find(({variant}) => variant.options.every((option, k) => option === wanted[k]));
          return (exact?.variant.available && exact) || candidates.find(({variant}) => variant.available) || exact || candidates[0];
        };
        const choose = (position, value) => {
          const pick = resolve(position, value);
          if (!pick) return;
          selector.selectedIndex = pick.index;
          selector.dispatchEvent(new Event('change', {bubbles: true}));
        };
        const steps = optionNames.map((name, position) => {
          const values = [...new Set(variants.map(variant => variant.options[position]))];
          const fieldset = document.createElement('fieldset');
          fieldset.className = 'dg-condition-picker dg-step-picker';
          const legend = document.createElement('legend');
          legend.textContent = name + ':';
          const panel = document.createElement('div');
          panel.className = 'dg-condition-panel';
          const choices = document.createElement('div');
          choices.className = 'dg-condition-choices';
          choices.setAttribute('role', 'radiogroup');
          choices.setAttribute('aria-label', name);
          choices.style.setProperty('--condition-columns', Math.min(values.length, 3));
          const prices = [];
          const buttons = values.map((value, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'dg-condition-tile';
            button.setAttribute('role', 'radio');
            const label = document.createElement('strong');
            label.textContent = value;
            const price = document.createElement('span');
            price.className = 'dg-condition-tile-price';
            prices.push(price);
            button.append(label, price);
            button.addEventListener('click', () => choose(position, value));
            // Hovering a choice previews the price that clicking it would select.
            const headlinePrice = this.querySelector('[data-dg-detail-price]');
            button.addEventListener('pointerenter', () => {
              const pick = resolve(position, value);
              if (!pick || !headlinePrice || pick.index === selector.selectedIndex) return;
              headlinePrice.textContent = pick.variant.price;
              headlinePrice.classList.add('is-preview');
            });
            button.addEventListener('pointerleave', () => {
              if (!headlinePrice) return;
              headlinePrice.textContent = variants[selector.selectedIndex].price;
              headlinePrice.classList.remove('is-preview');
            });
            button.addEventListener('keydown', event => {
              const direction = {ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1}[event.key];
              if (!direction) return;
              event.preventDefault();
              const visible = buttons.filter(item => !item.hidden);
              const target = visible[(visible.indexOf(button) + direction + visible.length) % visible.length];
              target.focus();
              target.click();
            });
            choices.append(button);
            return button;
          });
          panel.append(choices);
          fieldset.append(legend, panel);
          return {fieldset, values, buttons, prices};
        });
        const note = document.createElement('p');
        note.className = 'dg-condition-note';
        note.setAttribute('aria-live', 'polite');
        steps[steps.length - 1].fieldset.querySelector('.dg-condition-panel').append(note);
        optionLabel.after(...steps.map(step => step.fieldset));
        optionLabel.hidden = true;
        const soldOutLabel = selector.dataset.labelSoldOut || '';
        conditionPicker = {note, sync: () => {
          const selected = variants[selector.selectedIndex];
          steps.forEach((step, position) => {
            step.buttons.forEach((button, k) => {
              const value = step.values[k];
              const checked = selected.options[position] === value;
              const matches = variants.filter(variant => variant.options[position] === value && matchesBefore(variant, selected, position));
              const buyable = matches.filter(variant => variant.available);
              button.hidden = matches.length === 0;
              button.setAttribute('aria-checked', String(checked));
              button.tabIndex = checked ? 0 : -1;
              button.classList.toggle('is-unavailable', buyable.length === 0);
              // Prices live in the headline price; tiles only flag sold-out choices.
              step.prices[k].textContent = buyable.length ? '' : soldOutLabel;
              step.prices[k].hidden = buyable.length > 0;
            });
          });
        }};
      } else if (selector && selector.options.length > 1 && !('dgAuswahl' in selector.dataset)) { // Auswahl: up to 250 games stay a plain select
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'dg-condition-picker' + (isConditionOption ? '' : ' dg-variant-picker');
        const legend = document.createElement('legend');
        legend.textContent = optionName + ':';
        const panel = document.createElement('div');
        panel.className = 'dg-condition-panel';
        const choices = document.createElement('div');
        choices.className = 'dg-condition-choices';
        choices.setAttribute('role', 'radiogroup');
        choices.setAttribute('aria-label', optionName);
        choices.style.setProperty('--condition-columns', Math.min(isConditionOption ? 4 : 2, selector.options.length));
        const note = document.createElement('p');
        note.className = 'dg-condition-note';
        note.setAttribute('aria-live', 'polite');
        const buttons = Array.from(selector.options, (option, index) => {
          const value = JSON.parse(option.dataset.pdpValue);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'dg-condition-tile';
          button.setAttribute('role', 'radio');
          const name = document.createElement('strong');
          name.textContent = option.textContent.trim();
          const price = document.createElement('span');
          price.className = 'dg-condition-tile-price';
          const check = document.createElement('span');
          check.className = 'dg-condition-check';
          check.setAttribute('aria-hidden', 'true');
          check.textContent = '✓';
          price.append(check, document.createTextNode(value.price));
          button.append(name, price);
          button.addEventListener('click', () => {
            selector.selectedIndex = index;
            selector.dispatchEvent(new Event('change', {bubbles: true}));
          });
          button.addEventListener('keydown', event => {
            const direction = {ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1}[event.key];
            let target;
            if (direction) target = (index + direction + selector.options.length) % selector.options.length;
            else if (event.key === 'Home') target = 0;
            else if (event.key === 'End') target = selector.options.length - 1;
            else return;
            event.preventDefault();
            conditionPicker.buttons[target].focus();
            conditionPicker.buttons[target].click();
          });
          choices.append(button);
          return button;
        });
        panel.append(choices, note);
        fieldset.append(legend, panel);
        optionLabel.after(fieldset);
        optionLabel.hidden = true;
        conditionPicker = {buttons, note};
      }
      const setImage = url => {
        if (!main || !url) return;
        if (main.getAttribute('src') !== url) {
          main.removeAttribute('srcset');
          main.removeAttribute('sizes');
          main.src = url;
        }
        this.querySelectorAll('[data-dg-gallery]').forEach(button => {
          button.setAttribute('aria-pressed', String(button.dataset.dgGallery === url));
        });
        if (zoom.open) syncZoom();
      };
      const stepImage = step => {
        if (!zoom.open || galleryUrls.length < 2) return;
        zoomIndex = (zoomIndex + step + galleryUrls.length) % galleryUrls.length;
        setImage(galleryUrls[zoomIndex]);
      };
      previous.addEventListener('click', () => stepImage(-1));
      next.addEventListener('click', () => stepImage(1));
      zoom.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        stepImage(event.key === 'ArrowRight' ? 1 : -1);
      });
      const inCart = {}; // variant id → quantity in the cart since the last add; before that, the rendered in_cart
      const update = (changeUrl = false) => {
        const option = selector?.options[selector.selectedIndex];
        if (!option) return;
        const value = JSON.parse(option.dataset.pdpValue);
        this.querySelector('[data-dg-detail-price]').textContent = value.price;
        this.querySelector('[data-dg-detail-price]').classList.remove('is-preview');
        this.querySelector('[data-dg-detail-sku]').textContent = value.sku || '';
        const stock = this.querySelector('[data-dg-detail-stock]');
        stock.textContent = value.stock;
        stock.dataset.available = String(value.available);
        const notify = this.querySelector('[data-pdp-notify]');
        if (notify) {
          notify.hidden = value.available;
          const tag = notify.querySelector('[data-pdp-notify-tag]');
          if (tag) tag.value = `newsletter,notify-me,notify-${option.value}`;
        }
        if (conditionPicker?.sync) {
          conditionPicker.sync();
          const selectedName = document.createElement('strong');
          selectedName.textContent = option.textContent.trim().toLocaleUpperCase();
          conditionPicker.note.replaceChildren(selectedName, document.createTextNode(' · ' + value.stock));
        } else if (conditionPicker) {
          conditionPicker.buttons.forEach((button, index) => {
            const selected = index === selector.selectedIndex;
            button.setAttribute('aria-checked', String(selected));
            button.tabIndex = selected ? 0 : -1;
          });
          const selectedName = document.createElement('strong');
          selectedName.textContent = (isConditionOption ? (value.condition || option.textContent.trim()) : option.textContent.trim()).toLocaleUpperCase();
          conditionPicker.note.replaceChildren(selectedName, document.createTextNode(' · ' + value.stock));
        }
        const left = value.limit - (inCart[option.value] ?? value.in_cart);
        if (quantity) {
          quantity.max = Math.max(1, left);
          quantity.value = String(clampQuantity(quantity.value));
        }
        this.querySelector('[data-pdp-tax]').textContent = value.tax;
        setImage(value.image);
        const specification = Array.from(this.querySelectorAll('template[data-pdp-specs]')).find(t => t.dataset.pdpSpecs === option.value);
        if (specification) this.querySelector('[data-pdp-specifications]').replaceChildren(specification.content.cloneNode(true));
        if (cart) {
          cart.disabled = isPreview || !value.available || left <= 0;
          if (!isPreview) cart.textContent = !option.value ? this.dataset.chooseLabel : !value.available ? this.dataset.soldLabel : left <= 0 ? this.dataset.fullLabel : this.dataset.addLabel;
        }
        if (changeUrl && !isPreview) {
          const url = new URL(location.href);
          url.searchParams.set('variant', option.value);
          history.replaceState(history.state, '', url);
        }
      };
      selector?.addEventListener('change', () => update(true));
      this.querySelectorAll('[data-dg-gallery]').forEach(button => button.addEventListener('click', () => setImage(button.dataset.dgGallery)));
      const quantityNote = this.querySelector('[data-pdp-qty-note]');
      // Clamps to 1..stock; shows "max N" whenever the wanted amount was above the stock.
      const clampQuantity = value => {
        const max = Number(quantity.max) || 999;
        const wanted = Math.floor(Number(value) || 1);
        if (quantityNote) {
          quantityNote.hidden = wanted <= max;
          quantityNote.textContent = quantityNote.dataset.label.replace('[n]', max);
        }
        return Math.max(1, Math.min(max, wanted));
      };
      quantity?.addEventListener('change', () => { quantity.value = String(clampQuantity(quantity.value)); });
      this.querySelectorAll('[data-pdp-qty]').forEach(button => button.addEventListener('click', () => {
        quantity.value = String(clampQuantity(Number(quantity.value) + Number(button.dataset.pdpQty)));
      }));
      this.querySelector('form')?.addEventListener('submit', async event => {
        event.preventDefault();
        if (isPreview || cart?.disabled) return;
        const form = event.currentTarget;
        const data = new FormData(form);
        let events;
        try { events = await import('@shopify/events'); } catch { form.submit(); return; } // no JS events → classic post to /cart
        const { CartLinesUpdateEvent, CartErrorEvent } = events;
        const deferred = CartLinesUpdateEvent.createPromise();
        // Same event the theme's own forms send: updates header count and opens the "added to cart" popup.
        form.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(data.get('id')), quantity: Number(data.get('quantity')) || 1 }],
          promise: deferred.promise,
        }));
        try {
          const root = window.Shopify?.routes?.root || '/';
          const added = await fetch(`${root}cart/add.js`, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
          const error = added.ok ? null : await added.json().catch(() => ({}));
          const ajaxCart = await fetch(`${root}cart.js`).then(r => r.json());
          const id = String(data.get('id'));
          const option = Array.from(selector.options).find(o => o.value === id);
          const before = inCart[id] ?? JSON.parse(option.dataset.pdpValue).in_cart;
          Array.from(selector.options).forEach(o => {
            inCart[o.value] = ajaxCart.items.filter(i => String(i.variant_id) === o.value).reduce((sum, i) => sum + i.quantity, 0);
          });
          // Too few left: Shopify adds what is left and still answers 422. Anything added counts as success.
          const failed = !!error && inCart[id] <= before;
          // Popup (snippets/dg-cart-added.liquid) shows this message, e.g. "all available items already in cart".
          if (failed) form.dispatchEvent(new CartErrorEvent({ error: error.message || 'Add to cart failed', code: 'INVALID', detail: { description: error.description } }));
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart), detail: { didError: failed, itemCount: ajaxCart.item_count } });
          quantity.value = '1';
          update(); // new max / "all in cart" state for the selected variant
        } catch (err) {
          deferred.reject(err);
        }
      });
      zoomButton.disabled = !main;
      zoomButton.addEventListener('click', () => {
        if (!main) return;
        syncZoom();
        zoom.showModal();
      });
      this.querySelector('[data-pdp-zoom-close]').addEventListener('click', () => zoom.close());
      zoom.addEventListener('close', () => zoomButton.focus({preventScroll:true}));
      zoom.addEventListener('click', event => { if (event.target === zoom) zoom.close(); });
      update();
      // Shopify redirects to ?customer_posted=true after the notify signup; drop it so a reload shows the form again.
      const postedUrl = new URL(location.href);
      if (postedUrl.searchParams.has('customer_posted')) {
        postedUrl.searchParams.delete('customer_posted');
        history.replaceState(history.state, '', postedUrl);
      }
    }
    disconnectedCallback() {
      const zoom = this.querySelector('[data-pdp-zoom]');
      if (zoom?.open) zoom.close();
    }
  }
  customElements.define('dg-product-view', DualityProduct);
})();
